import { computed, Injectable, Signal, signal } from '@angular/core';

import { DEFAULT_LOADING_KEY, LoadingKey } from '@shared/types/loading-key.type';

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private readonly loadingState = signal<Partial<Record<LoadingKey, number>>>({});
  private readonly loadingSignals = new Map<LoadingKey, Signal<boolean>>();

  setLoading(key: LoadingKey | null): void {
    const loadingKey = key ?? DEFAULT_LOADING_KEY;
    this.loadingState.update((state) => {
      const currentCount = state[loadingKey] ?? 0;
      return { ...state, [loadingKey]: currentCount + 1 };
    });
  }

  setLoaded(key: LoadingKey | null): void {
    const loadingKey = key ?? DEFAULT_LOADING_KEY;
    this.loadingState.update((state) => {
      const { [loadingKey]: currentCount, ...rest } = state;
      if (!currentCount) {
        return state;
      }
      const newCount = currentCount - 1;
      if (newCount === 0) {
        return rest;
      }
      return { ...rest, [loadingKey]: newCount };
    });
  }

  isLoading(key: LoadingKey = DEFAULT_LOADING_KEY): Signal<boolean> {
    let signal = this.loadingSignals.get(key);
    if (!signal) {
      signal = computed(() => {
        const state = this.loadingState();
        const count = state[key] ?? 0;
        if (key === DEFAULT_LOADING_KEY) {
          return count > 0 || Object.values(state).some((value) => value > 0);
        }
        return count > 0;
      });
      this.loadingSignals.set(key, signal);
    }
    return signal;
  }
}
